"use client";

import { useCookieConsent } from "@/hooks/useCookieConsent";
import { useEffect } from "react";

const BEACON_SRC = "https://static.cloudflareinsights.com/beacon.min.js";

/**
 * Loads the Cloudflare Insights beacon once analytics consent is accepted.
 *
 * Appended from the browser rather than rendered on the server: the layout no
 * longer reads the consent cookie during render, so a cached page cannot decide
 * whether to emit this script. Gating it server-side meant a cached page never
 * emitted the beacon for anyone, accepted or not.
 *
 * On decline/reset the script tag is removed. That stops further loads; any
 * work already started by the vendor may continue until the next navigation.
 */
export function CloudflareBeacon({ token }: { token: string }) {
  const { consent, loaded } = useCookieConsent();

  useEffect(() => {
    if (!loaded) return;

    const existing = document.querySelector(`script[src="${BEACON_SRC}"]`);
    if (consent !== "accepted") {
      existing?.remove();
      return;
    }
    if (existing) return;

    const script = document.createElement("script");
    script.src = BEACON_SRC;
    script.defer = true;
    script.setAttribute("data-cf-beacon", JSON.stringify({ token }));
    document.head.appendChild(script);
  }, [consent, loaded, token]);

  return null;
}
